import { Link } from "react-router-dom";
import { useState } from "react";
import FrameOverlay from "@/components/base/FrameOverlay";
import FadeIn from "@/components/base/FadeIn";

interface Relic {
  src: string;
  title: string;
  detail: string;
}

const relics: Relic[] = [
  {
    src: "https://readdy.ai/api/search-image?query=Black%20and%20grey%20realism%20tattoo%20of%20classical%20marble%20sculpture%20dramatic%20Rembrandt%20lighting%20deep%20shadows%20sculptural%20volume%20warm%20terracotta%20undertone%20professional%20editorial%20photography%20museum%20quality%20chiaroscuro%20effect%20on%20skin&width=700&height=900&seq=ap3-relic-001&orientation=portrait",
    title: "Marble Study",
    detail: "Full Sleeve",
  },
  {
    src: "https://readdy.ai/api/search-image?query=Black%20and%20grey%20baroque%20still%20life%20vanitas%20tattoo%20with%20skull%20and%20wilting%20flowers%20dramatic%20chiaroscuro%20deep%20shadow%20work%20sculptural%20quality%20warm%20bronze%20tones%20professional%20editorial%20photography&width=700&height=900&seq=ap3-relic-002&orientation=portrait",
    title: "Vanitas Noir",
    detail: "Chest Piece",
  },
  {
    src: "https://readdy.ai/api/search-image?query=Blackwork%20geometric%20tattoo%20with%20architectural%20column%20motifs%20classical%20proportions%20deep%20black%20ink%20precise%20sharp%20lines%20sculptural%20dimension%20warm%20skin%20tones%20professional%20editorial%20photography&width=700&height=900&seq=ap3-relic-003&orientation=portrait",
    title: "Architectural Form",
    detail: "Leg Piece",
  },
  {
    src: "https://readdy.ai/api/search-image?query=Black%20and%20grey%20Greek%20god%20statue%20thigh%20tattoo%20dramatic%20lighting%20deep%20shadows%20classical%20renaissance%20sculptural%20realism%20warm%20tones%20professional%20editorial%20photography%20museum%20quality%20large%20format%20piece&width=700&height=900&seq=ap3-relic-004&orientation=portrait",
    title: "Deus ex Umbra",
    detail: "Thigh Panel",
  },
];

export default function ArtistProfile3() {
  const [hover, setHover] = useState<number | null>(null);
  const [doorOpen, setDoorOpen] = useState(false);

  const handleEnter = () => {
    const link = document.createElement("a");
    link.href = "/artist/cassius-vel";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <section className="bg-foreground-950 text-background-50 py-24 md:py-36 border-t border-foreground-950/40 overflow-hidden relative">
      {/* Texture overlay for stone feel */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, #C8A070 0, #C8A070 1px, transparent 1px, transparent 6px)",
        }}
      />

      {/* Hieroglyphic door overlay */}
      {doorOpen && (
        <div className="fixed inset-0 z-[100] pointer-events-none">
          <div className="absolute inset-y-0 left-0 w-1/2 animate-door-left fa-door-panel overflow-hidden">
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 opacity-70">
              {[...Array(6)].map((_, r) => (
                <div key={r} className="flex gap-6 animate-hiero-glow" style={{ animationDelay: `${r * 0.3}s` }}>
                  {[...Array(4)].map((_, c) => (
                    <span key={c} className="font-display text-4xl md:text-6xl" style={{ animationDelay: `${r * 0.3 + c * 0.1}s` , color: "#B5722E" }}>
                      {["𓂀", "𓋹", "𓆣", "𓅓"][c]}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div className="absolute inset-y-0 right-0 w-1/2 animate-door-right fa-door-panel overflow-hidden">
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 opacity-70">
              {[...Array(6)].map((_, r) => (
                <div key={r} className="flex gap-6 animate-hiero-glow" style={{ animationDelay: `${r * 0.3 + 0.15}s` }}>
                  {[...Array(4)].map((_, c) => (
                    <span key={c} className="font-display text-4xl md:text-6xl" style={{ animationDelay: `${r * 0.3 + c * 0.1 + 0.15}s` , color: "#B5722E" }}>
                      {["𓃭", "𓁈", "𓄿", "𓃒"][c]}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-2 h-full bg-gradient-to-b from-transparent via-[#B5722E]/70 to-transparent animate-hiero-glow" />
          </div>
        </div>
      )}

      <div className="px-6 md:px-12 max-w-7xl mx-auto relative">
        {/* Heading */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div>
            <p className="font-display text-[11px] uppercase tracking-[0.28em] text-background-50/50 mb-5">
              ( 03 ) — The Chiaroscuro Collection
            </p>
            <h3 className="font-display font-black leading-[0.9] tracking-[-0.04em] text-5xl md:text-6xl lg:text-7xl">
              Cassius<em className="font-serif italic font-light"> Vel</em>
            </h3>
          </div>
          <div className="max-w-md">
            <p className="text-base md:text-lg text-background-50/70 leading-relaxed font-serif mb-6">
              A devotee of shadow — Cassius brings a sculptor&apos;s understanding of volume to black and grey. <em className="italic">Sweep your cursor across each relic to illuminate.</em>
            </p>
            <button
              onClick={handleEnter}
              className="group inline-flex items-center gap-3 cursor-pointer"
            >
              <span className="font-display text-xs uppercase tracking-[0.25em] text-background-50 border-b border-background-50/40 pb-1 group-hover:border-background-50 transition-colors whitespace-nowrap">
                Enter the Gallery
              </span>
              <span className="w-9 h-9 rounded-full border border-background-50/30 flex items-center justify-center text-background-50 transition-all duration-500 group-hover:bg-background-50 group-hover:text-foreground-950">
                <i className="ri-arrow-right-up-line text-sm"></i>
              </span>
            </button>
          </div>
        </div>

        {/* Relics row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-product-shop>
          {relics.map((r, i) => {
            const active = hover === i;
            return (
              <FadeIn key={i} delay={i * 100} className="group block cursor-pointer">
                <div
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(null)}
                  onClick={handleEnter}
                >
                  <div className="relative aspect-[3/4] bg-foreground-950 overflow-hidden border border-background-50/15">
                    <img
                      src={r.src}
                      alt={r.title}
                      className={`w-full h-full object-cover object-top transition-all duration-1000 ease-out ${
                        active ? "scale-100" : "scale-105"
                      }`}
                      style={{
                        filter: active ? "grayscale(0) brightness(1)" : "grayscale(0.85) brightness(0.55)",
                      }}
                    />
                    <FrameOverlay />

                    {/* Dust veil */}
                    <div
                      className="absolute inset-0 pointer-events-none transition-opacity duration-1000"
                      style={{
                        opacity: active ? 0 : 0.55,
                        background:
                          "radial-gradient(circle at 30% 20%, rgba(244,240,232,0.18), transparent 60%), radial-gradient(circle at 70% 80%, rgba(244,240,232,0.12), transparent 50%), rgba(10,10,10,0.4)",
                      }}
                    />

                    {/* Crack lines on hover */}
                    <div
                      className={`absolute inset-0 pointer-events-none transition-opacity duration-700 ${
                        active ? "opacity-30" : "opacity-0"
                      }`}
                      style={{
                        backgroundImage:
                          "linear-gradient(118deg, transparent 48%, rgba(200,160,112,0.6) 49%, transparent 50%), linear-gradient(72deg, transparent 65%, rgba(200,160,112,0.4) 66%, transparent 67%)",
                      }}
                    />

                    <div className="absolute top-3 right-3 z-10 px-2 py-1 bg-foreground-950/70 border border-accent-500/30 text-[9px] uppercase tracking-[0.2em] font-display text-accent-500">
                      {r.detail}
                    </div>

                    <div className="absolute top-3 left-3 z-10 font-display text-[10px] uppercase tracking-[0.25em] text-background-50/70">
                      N&ordm; {String(i + 1).padStart(2, "0")}
                    </div>

                    <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 bg-gradient-to-t from-foreground-950 via-foreground-950/70 to-transparent">
                      <p className="font-serif italic text-sm md:text-base text-background-50 leading-tight">
                        {r.title}
                      </p>
                      <p className="font-display text-[9px] uppercase tracking-[0.25em] text-background-50/50 mt-1">
                        Chiaroscuro — 2035
                      </p>
                    </div>
                  </div>
                </div>
              </FadeIn>
            );
          })}
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mt-12 border-t border-background-50/15 pt-6">
          <p className="font-serif italic text-sm text-background-50/60">
            &ldquo;Shadow is not the absence of light — it is its partner.&rdquo; — Cassius Vel
          </p>
          <p className="font-display text-[10px] uppercase tracking-[0.28em] text-background-50/50">
            04 works on display
          </p>
        </div>
      </div>
    </section>
  );
}